import clsx from 'clsx';
import { Navigate, Outlet, useParams, useLocation, matchPath } from '@/util/react-router-dom';
import styles from './branding-outlet.module.css';
import { createContext, ReactNode, useContext } from 'react';
import { useGetNetworksConfiguration } from '@/api/get-network-configuration';
import { GetNetworkConfigurationErrorType, NetworkConfigurationDto } from '@/api/api.schemas';
import { env } from '@/env';
import { PreprocessRootRedirect } from '@/pages/preprocess-root-redirect';
import { useCurrentUser } from '@/contexts/current-user-context';
import { isUserLoggedIn } from '@/util/helpers';
import { BrandRedirect } from '@/components/brand-redirect';

const brands = [
  'ault',
  'avera',
  'bcbsne',
  'bhskin',
  'bostonu',
  'default',
  'dermatologyconsultants',
  'dummy-test-doc',
  'floridablue',
  'general',
  'highmark',
  'lho',
  'mdlive',
  'nalc',
  'surest',
  'uhc',
  'uhsm',
] as const;

export type Brand = (typeof brands)[number];

const noBrandingNetworks = [
  'default',
  'dummy-test-doc',
  'general',
  'surest',
  'uhc',
  'uhsm',
] as const;
type NoBrandingNetwork = (typeof noBrandingNetworks)[number];

export function isBrand(name: string): name is Brand {
  return brands.includes(name as Brand);
}

function hasBranding(name: string): name is Exclude<Brand, NoBrandingNetwork> {
  return !noBrandingNetworks.includes(name as NoBrandingNetwork);
}

function getEncounterIdFromPath(pathname: string): number | undefined {
  const visitId = matchPath({ path: '/visit/:visitId/*' }, pathname)?.params.visitId;
  const visitIdNum = visitId ? Number(visitId) : undefined;
  return visitIdNum && !isNaN(visitIdNum) ? visitIdNum : undefined;
}

function isInDifferentNetwork(userNetwork?: string, currentNetwork?: string): userNetwork is Brand {
  return (
    !!userNetwork && !!currentNetwork && isBrand(userNetwork) && userNetwork !== currentNetwork
  );
}

export function BrandingOutlet() {
  const { name } = useParams();
  const location = useLocation();
  const { user } = useCurrentUser();

  if (!name || !isBrand(name)) {
    return <PreprocessRootRedirect to="/default" replace doNotResolveBrandingPath />;
  }

  if (
    isUserLoggedIn(user) &&
    !getEncounterIdFromPath(location.pathname) &&
    isInDifferentNetwork(user.network, name)
  ) {
    return <BrandRedirect brand={user.network} />;
  }

  return (
    <div className={clsx(styles.branding, hasBranding(name) ? styles[name] : styles.default)}>
      <BrandingProvider name={name}>
        <Outlet />
      </BrandingProvider>
    </div>
  );
}

type BrandingContextType = NetworkConfigurationDto & {
  brandName: Brand;
  hasInsurance: boolean;
  hasIdVerification: boolean;
  refetch: () => void;
};

const BrandingContext = createContext<BrandingContextType | null>(null);

// 5 minutes
const REFRESH_INTERVAL = 1000 * 60 * 5;

export function BrandingProvider({ name, children }: { name: Brand; children: ReactNode }) {
  const location = useLocation();
  const encounterId = getEncounterIdFromPath(location.pathname);

  const { data, error, refetch } = useGetNetworksConfiguration(
    name,
    { encounterId },
    { query: { refetchInterval: REFRESH_INTERVAL } },
  );

  if (error && error.type === GetNetworkConfigurationErrorType.NotFound) {
    return <Navigate to="/default" replace doNotResolveBrandingPath />;
  }

  if (!data) {
    return null;
  }

  return (
    <>
      <title>{data.configuration.appTitle}</title>
      <link
        rel="icon"
        type="image/svg+xml"
        href={`${env.VITE_BRANDING_ASSETS_BASE_PATH}/${name}/favicon.ico`}
      />
      <BrandingContext.Provider
        value={{
          ...data.configuration,
          brandName: name,
          hasInsurance: data.configuration.insuranceActive,
          hasIdVerification: data.configuration.identityVerificationActive,
          refetch,
        }}
      >
        {children}
      </BrandingContext.Provider>
    </>
  );
}

export function useBranding() {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }

  const { brandName, logoUrl, ...rest } = context;

  return {
    brandName,
    ...rest,
    logoUrl: logoUrl ?? `${env.VITE_BRANDING_ASSETS_BASE_PATH}/${brandName}/logo.png`,
  };
}
